import { useEffect, useRef, useState } from "react";
import { updateDataLayer } from "@utils/updateDataLayer";
import { getPaywallCountdown, getPaywallType } from "src/appConfig";
import styles from "./styles.module.scss";
import {
  Guarantee,
  PersonalPlan,
  Plan,
  Rate,
  ResultsSlider,
  Reviews,
  Timer,
  Transform,
} from "../../shared/components";

import Normal from "./assets/img/normal.png";
import Overweight from "./assets/img/overweight.png";
import Obese from "./assets/img/obese.png";
import Underweight from "./assets/img/underweight.png";
import Lean from "./assets/img/lean.png";
import Toned from "./assets/img/toned.png";
import Average from "./assets/img/average.png";
import SlightlyLighter from "./assets/img/few_pounds_less.png";
import { ReliefSlider } from "./components/lastingRelief";
import { UpsaleModal } from "../../shared/components/upsaleModal";
import { CalorieCounter } from "../../shared/components/calorieCounter";
import { AdditionalDiscount } from "../../shared/components/additionalDiscount";
import { Benefits } from "./components/benefits";
import useLocationEvent from "@hooks/useLocationEvent";
import { DiscountProvider } from "@hooks/useDiscount";

const BMI_TYPE_IMAGES = (desiredBodyType) => {
  const After = {
    Lean,
    Toned,
    Average,
    SlightlyLighter,
  };
  return {
    NORMAL: Normal,
    OVERWEIGHT: Overweight,
    OBESE: Obese,
    UNDERWEIGHT: Underweight,
    After: After[desiredBodyType.replace(/\\?"/g, "")],
  };
};

export const Paywall = () => {
  const storedCountdown = getPaywallCountdown();
  const parsedCountdown = storedCountdown
    ? parseInt(storedCountdown, 10)
    : null;

  const skipTimer = parseInt(parsedCountdown) === -1;

  const [upsaleModalVisible, setUpsaleModalVisible] = useState(false);
  const [showTimer, setShowTimer] = useState(false);
  const profileCardRef = useRef(null);
  const planRef = useRef(null);

  useLocationEvent();

  useEffect(() => {
    if(skipTimer) return;

    const handleScroll = () => {
      if (profileCardRef.current && !showTimer) {
        const rect = profileCardRef.current.getBoundingClientRect();
        const isVisible = rect.top <= window.innerHeight && rect.bottom >= 0;

        if (isVisible) {
          setShowTimer(true);
          window.removeEventListener("scroll", handleScroll);
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleScrollToPlan = () => {
    planRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const name = localStorage.getItem("name");

  const additionalDiscount =
    localStorage.getItem("additionalDiscount") === "true";

  const handleCheckoutClose = () => {
    setUpsaleModalVisible(true);
  };

  useEffect(() => {
    document.body.style.overflow = upsaleModalVisible ? "hidden" : "auto";
  }, [upsaleModalVisible]);

  return (
    <DiscountProvider>
      <div className={styles.layout}>
        {showTimer && (
          <Timer
            onGetMyPlanClick={handleScrollToPlan}
            onTimeEnd={() => setShowTimer(false)}
            customTime={parsedCountdown * 1000}
          />
        )}
        {additionalDiscount && <AdditionalDiscount />}
        {!additionalDiscount && (
          <>
            <Transform
              images={BMI_TYPE_IMAGES(
                localStorage.getItem("targetBody") || "Toned"
              )}
              title="Fitness"
              header="After the program"
            />
            <PersonalPlan />
          </>
        )}
        <div ref={profileCardRef}>
          <CalorieCounter />
        </div>
        <div ref={planRef}>
          <Plan
            id={1}
            title="AI-optimized plan"
            isMetrics
            onCloseCheckout={handleCheckoutClose}
            additionalDiscount={additionalDiscount}
            showTimer={showTimer}
          />
        </div>
        <ReliefSlider />
        <Rate />
        <Benefits />
        <Reviews />
        <ResultsSlider />

        <Plan
          id={2}
          isMetrics
          title="Get visible results in 4 weeks with AI‑optimized plan!"
          name={name}
          onCloseCheckout={handleCheckoutClose}
          additionalDiscount={additionalDiscount}
          showTimer={showTimer}
        />
        <Guarantee />
        <footer className={styles.footer}>
          Straiton Limited | Alpha Tower, Floor 1, Office 11, <br /> Pavlou
          Nirvana- Aipeias, Limassol, 3021, Cyprus, Test
        </footer>
      </div>
      {upsaleModalVisible && (
        <UpsaleModal
          onClose={() => setUpsaleModalVisible(false)}
          text="Personalized Exercise Plan."
        />
      )}
    </DiscountProvider>
  );
};
